import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AnalyticsOutlined,
  Close,
  DashboardOutlined,
  GppGoodOutlined,
  Logout,
  Menu,
  PlayArrowRounded,
  PersonOutline,
  SellOutlined,
  WorkOutline,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useAuthSession, useUser } from "../context/AuthContext";

const appNavItems = [
  { label: "Dashboard", path: "/dashboard", icon: <DashboardOutlined /> },
  { label: "Analytics", path: "/analytics", icon: <AnalyticsOutlined /> },
  { label: "Interviews", path: "/interviews", icon: <WorkOutline /> },
  { label: "Profile", path: "/onboarding", icon: <PersonOutline /> },
  { label: "Pricing", path: "/pricing", icon: <SellOutlined /> },
];

const marketingNavItems = [
  { label: "Features", href: "#features" },
  { label: "Interview Tracks", href: "#tracks" },
  { label: "Pricing", path: "/pricing" },
  { label: "Resources", href: "#resources" },
  { label: "Dashboard", path: "/dashboard" },
];

function Brand({ compact = false, onClick }) {
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        alignItems: "center",
        appearance: "none",
        background: "none",
        border: 0,
        color: "text.primary",
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        gap: 1,
        p: 0,
        textAlign: "left",
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 4 },
      }}
    >
      <GppGoodOutlined sx={{ color: "primary.main", fontSize: compact ? 28 : 31 }} />
      <Typography sx={{ fontSize: compact ? 16 : 17, fontWeight: 800, whiteSpace: "nowrap" }}>
        Evaluate Yourself
      </Typography>
    </Box>
  );
}

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthSession();
  const { isSignedIn } = useUser();
  const isMarketing = location.pathname === "/";

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate(isMarketing ? "/" : "/login");
    setDrawerOpen(false);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const marketingLinks = (
    <List sx={{ px: 2, py: 1 }}>
      {marketingNavItems.map((item) => (
        <ListItem key={item.label} disablePadding>
          <ListItemButton
            component={item.href ? "a" : RouterLink}
            href={item.href}
            to={item.path}
            onClick={closeDrawer}
            sx={{ borderRadius: 1, minHeight: 48, "& .MuiSvgIcon-root": { fontSize: 14 } }}
          >
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 650 }} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  const appLinks = (
    <List sx={{ px: 2, py: 1 }}>
      {appNavItems.map((item) => {
        const active = location.pathname === item.path;
        return (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={active}
              onClick={() => handleNavigation(item.path)}
            sx={{ borderRadius: 1, minHeight: 48, "&.Mui-selected": { bgcolor: "#F5F5F5" }, "& .MuiSvgIcon-root": { fontSize: 14 } }}
            >
              <ListItemIcon sx={{ color: active ? "primary.main" : "text.secondary", minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: active ? 700 : 500 }} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <>
      <AppBar
        position={isMarketing ? "relative" : "sticky"}
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 0 rgba(15,23,42,.02)",
          color: "text.primary",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 68, md: 82 },
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 0 },
            width: "min(100% - 32px, 1440px)",
          }}
        >
          <Brand onClick={() => navigate("/")} />

          {!isMobile && isMarketing && (
            <Box component="nav" aria-label="Primary navigation" sx={{ display: "flex", gap: 4.5, ml: "auto", mr: { md: 5, lg: 9 } }}>
              {marketingNavItems.map((item) => (
                <Button
                  key={item.label}
                  component={item.href ? "a" : RouterLink}
                  href={item.href}
                  to={item.path}
                  color="inherit"
                  sx={{ minHeight: 40, p: 0, fontSize: 14, fontWeight: 650 }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {!isMobile && !isMarketing && (
            <Box component="nav" aria-label="Application navigation" sx={{ display: "flex", gap: 0.5, ml: "auto", mr: 3 }}>
              {appNavItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  startIcon={item.icon}
                  color={location.pathname === item.path ? "primary" : "inherit"}
                  sx={{ px: 1.5, "& .MuiSvgIcon-root": { fontSize: 14 } }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {!isMobile && isMarketing && (
            <Box sx={{ alignItems: "center", display: "flex", gap: 2 }}>
              {isSignedIn ? (
                <>
                  <Button color="inherit" onClick={() => navigate("/dashboard")}>Go to app</Button>
                  <Button variant="outlined" onClick={handleLogout}>Logout</Button>
                </>
              ) : (
                <>
                  <Button component={RouterLink} to="/login" color="inherit">Sign in</Button>
                  <Button component={RouterLink} to="/register" variant="contained" sx={{ px: 3.5 }}>Start Free</Button>
                </>
              )}
            </Box>
          )}

          {!isMobile && !isMarketing && (
            <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
              <Button variant="contained" startIcon={<PlayArrowRounded />} onClick={() => handleNavigation("/interview-config")}>
                New Interview
              </Button>
              <Button variant="outlined" color="error" startIcon={<Logout />} onClick={handleLogout}>
                Logout
              </Button>
            </Box>
          )}

          {isMobile && (
            <IconButton aria-label="Open navigation" onClick={() => setDrawerOpen(true)} sx={{ ml: "auto", color: "primary.main" }}>
              <Menu />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { borderRadius: 0, borderLeft: "1px solid", borderColor: "divider", width: "min(86vw, 320px)" } }}
      >
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", p: 2 }}>
          <Brand compact />
          <IconButton aria-label="Close navigation" onClick={closeDrawer}><Close /></IconButton>
        </Box>
        <Divider />
        {isMarketing ? marketingLinks : appLinks}
        <Divider />
        <Box sx={{ display: "grid", gap: 1.5, p: 2 }}>
          {isMarketing && !isSignedIn ? (
            <>
              <Button component={RouterLink} to="/login" onClick={closeDrawer} variant="outlined">Sign in</Button>
              <Button component={RouterLink} to="/register" onClick={closeDrawer} variant="contained">Start Free</Button>
            </>
          ) : (
            <>
              <Button variant="contained" startIcon={<PlayArrowRounded />} onClick={() => handleNavigation(isMarketing ? "/dashboard" : "/interview-config")}>
                {isMarketing ? "Go to app" : "New Interview"}
              </Button>
              <Button variant="outlined" color="error" startIcon={<Logout />} onClick={handleLogout}>Logout</Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
